import React, { PropsWithChildren, ReactNode, useLayoutEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { BsStopwatch } from '@react-icons/all-files/bs/BsStopwatch';
import cn from 'classnames';
import { FONT_STYLE } from '@bucketplace/design-system';

import { Duration, EditorPlaceholder } from '../../components';
import { useEditorNode } from '../../hooks';
import { DocumentPath } from '../../types';
import { convertRemainTime } from '../../utils';

export interface CountdownProps {
  duration?: Duration;
  subText?: string;
  timerPrefix: 'start' | 'end';
  align: 'left' | 'center';
  color: string;
  backgroundImage?: string;
  path: DocumentPath;
}

/** 종료 임박 시간 - 3시간 */
const IMMINENT_TIME = 3 * 60 * 60;
export const TIMER_PREFIX_MAP: { [key in CountdownProps['timerPrefix']]: string } = {
  start: '오픈까지',
  end: '종료까지',
};

export function Countdown({
  duration,
  subText,
  timerPrefix,
  align,
  color,
  path,
  backgroundImage,
}: PropsWithChildren<CountdownProps>) {
  const [timeGap, setTimeGap] = useState(-1);
  const { displayMode, displayDate } = useEditorNode(path);
  const interval = useRef<number>();

  const isInvalid = !duration?.startAt || !duration?.endAt;
  const isEditor = displayMode === 'edit';

  useLayoutEffect(() => {
    const { startAt, endAt } = duration ?? {};

    // 에디터 화면에거나 시작/종료일 미선택 시, interval 동작 안함
    if (!startAt || !endAt) {
      return;
    }

    const compareDisplayDateToDuration = () => {
      const startDate = new Date(startAt);
      const currentDate = displayDate ?? new Date();
      const diff = timerPrefix === 'start'
        ? new Date(startAt).getTime() - currentDate.getTime()
        : new Date(endAt).getTime() - currentDate.getTime();

      if (timerPrefix === 'start' && currentDate >= startDate || diff <= 0) {
        setTimeGap(0);
        clearInterval(interval.current);
      } else {
        setTimeGap(diff);
      }
    };

    compareDisplayDateToDuration();

    if (isEditor) {
      return;
    }

    interval.current = window.setInterval(compareDisplayDateToDuration, 1000);

    return () => clearInterval(interval.current);
  }, [duration?.startAt, duration?.endAt, displayDate]);

  if (isEditor && isInvalid) {
    return (
      <EditorPlaceholder icon={<BsStopwatch />} message="시간을 입력해주세요" />
    );
  }

  /**
   * 1. 서버 시간 가져옴
   * 2. 종료시간 - 현재시간 기준으로 날짜 계산 (function) ==> state
   */
  function getDisplayRemainTime(): ReactNode {
    const remainTime = convertRemainTime(timeGap);

    const { days, hours, minutes, seconds } = remainTime;
    const isImminent = timeGap / 1000 < IMMINENT_TIME && timeGap > 0;
    return days >= 1
      ? (
        <TimeText>{ `D-${days}` }</TimeText>
      )
      : (
        <TimeText className={cn({ primary: isImminent, end: timeGap <= 0 })}>
          { `${hours}`.padStart(2, '0') }
          :
          { `${minutes}`.padStart(2, '0') }
          :
          { `${seconds}`.padStart(2, '0') }
        </TimeText>
      );
  }

  return (
    <Container backgroundImage={backgroundImage} color={color} align={align}>
      { subText && <SubText>{ subText }</SubText> }
      <MainTitle>
        { TIMER_PREFIX_MAP[timerPrefix] }
        { getDisplayRemainTime() }
      </MainTitle>
    </Container>
  );
}

const Container = styled.section<{
  backgroundImage?: string,
  color: CountdownProps['color'],
  align: CountdownProps['align'],
}>`
  width: 100%;
  padding: 36px 16px 16px 16px;
  ${({ backgroundImage }) => backgroundImage && `
    background-image: url(${backgroundImage});
    background-size: 100% 100%;
  `}
  background-repeat: no-repeat;
  color: ${({ color }) => color};
  text-align: ${({ align }) => align};

  @media (min-width: 688px) {
    padding-top: 60px;
  }
`;

const SubText = styled.p`
  ${FONT_STYLE.body1_small};
  font-weight: 400;
  margin-bottom: 4px;

  @media (min-width: 688px) {
    ${FONT_STYLE.title2};
  }  
`;

const MainTitle = styled.p`
  ${FONT_STYLE.title2};
  font-weight: bold;

  @media (min-width: 688px) {
    ${FONT_STYLE.largeTitle};
  }  
`;

const TimeText = styled.span`
  margin-left: 4px;

  &.primary {
    color: ${({ theme }) => theme.content.primary2.thick};
  }

  &.end {
    color: ${({ theme }) => theme.content.base2};
  }
`;
